import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getCompletion, getExperienceKey, type ExperienceRow } from "@/lib/experience";

interface ExperienceCompletionProps {
  organization: string;
  major?: string | null;
  experienceKey?: string | null;
  type?: string | null;
  onHelp?: () => void;
  className?: string;
}

const ExperienceCompletion = ({
  organization,
  major,
  experienceKey,
  type,
  onHelp,
  className,
}: ExperienceCompletionProps) => {
  const key = experienceKey || getExperienceKey(organization, major);

  const [row, setRow] = useState<ExperienceRow | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let active = true;
    const load = async () => {
      const { data } = await supabase
        .from("experiences_public" as any)
        .select("*")
        .eq("experience_key", key)
        .maybeSingle();
      if (!active) return;
      setRow((data as any) ?? null);
      setLoaded(true);
    };
    load();
    return () => {
      active = false;
    };
  }, [key]);

  if (!loaded) return null;

  const { pct, missing } = getCompletion(row, type);
  const barColor = pct >= 80 ? "bg-green-600" : pct >= 50 ? "bg-yellow-500" : "bg-red-600";

  return (
    <div className={`rounded-lg border border-border bg-muted/30 p-3 ${className ?? ""}`}>
      <div className="flex items-baseline justify-between gap-2 mb-1.5">
        <span className="text-xs font-medium text-foreground truncate">
          {organization}
          {major ? ` — ${major}` : ""}
        </span>
        <span className="text-xs font-bold text-foreground shrink-0">{pct}% complete</span>
      </div>
      <div className="h-1.5 rounded-full bg-muted mb-2">
        <div className={`h-full rounded-full transition-all ${barColor}`} style={{ width: `${pct}%` }} />
      </div>

      {missing.length > 0 ? (
        <>
          <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1.5">Still missing</p>
          <div className="flex flex-wrap gap-1.5 mb-2.5">
            {missing.map((m) => (
              <Badge key={m} variant="outline" className="text-[10px] rounded-full px-2 font-normal">
                {m}
              </Badge>
            ))}
          </div>
          {onHelp && (
            <Button size="sm" onClick={onHelp} className="w-full gap-1.5 rounded-lg text-xs">
              <Sparkles className="w-3.5 h-3.5" />
              Help get this experience to 100%
            </Button>
          )}
        </>
      ) : (
        <p className="text-xs text-muted-foreground">
          Fully documented — thanks to students like you. 🎉
        </p>
      )}
    </div>
  );
};

export default ExperienceCompletion;
